/**
 * Multi-country tax regime catalog used by the Manual Invoice form.
 *
 * Shape:
 *   countries: array of {code, label, currency, subregimes: [...]}
 *   subregimes: each entry is { id, label, rate (% number) | null,
 *                                breakdown: [{label, rate}] | null,
 *                                fixed_label }
 *
 * Why a static module instead of a backend endpoint?
 * Tax rates change rarely (annual at most) and the form is the only
 * consumer — shipping a JS constant avoids an extra round-trip and
 * keeps the form snappy. When a rate moves we update this file.
 *
 * Notes:
 *   - For provinces with two distinct levies (QC TPS+TVQ, BC/SK/MB
 *     GST+PST/RST), `breakdown` carries the line-by-line decomposition
 *     for display; the backend only stores the aggregated tax_rate +
 *     tax_amount + tax_label.
 *   - USA is an explicit "exempt by default" entry — sales tax varies
 *     by ~10000 jurisdictions so we never pre-fill. The form disables
 *     the tax fields and shows a helper text.
 *   - "Autre" lets the user enter a free-form label + rate (e.g. VAT,
 *     GST, IVA in any unsupported jurisdiction).
 */

export const TAX_COUNTRIES = [
  {
    code: "CA",
    label: "Canada",
    currency: "CAD",
    subregimes: [
      {
        id: "QC", label: "Québec (TPS+TVQ)",
        rate: 14.975, fixed_label: "TPS+TVQ (QC)",
        breakdown: [
          { label: "TPS", rate: 5 },
          { label: "TVQ", rate: 9.975 },
        ],
      },
      { id: "ON", label: "Ontario (HST 13%)",     rate: 13, fixed_label: "HST (ON)" },
      { id: "NB", label: "Nouveau-Brunswick (HST 15%)", rate: 15, fixed_label: "HST (NB)" },
      { id: "NL", label: "Terre-Neuve-et-Labrador (HST 15%)", rate: 15, fixed_label: "HST (NL)" },
      { id: "NS", label: "Nouvelle-Écosse (HST 15%)", rate: 15, fixed_label: "HST (NS)" },
      { id: "PEI", label: "Île-du-Prince-Édouard (HST 15%)", rate: 15, fixed_label: "HST (PEI)" },
      { id: "AB", label: "Alberta (TPS 5%)", rate: 5, fixed_label: "TPS (AB)" },
      { id: "NT", label: "Territoires du Nord-Ouest (TPS 5%)", rate: 5, fixed_label: "TPS (NT)" },
      { id: "YK", label: "Yukon (TPS 5%)", rate: 5, fixed_label: "TPS (YK)" },
      { id: "NU", label: "Nunavut (TPS 5%)", rate: 5, fixed_label: "TPS (NU)" },
      {
        id: "BC", label: "Colombie-Britannique (TPS+PST)",
        rate: 12, fixed_label: "TPS+PST (BC)",
        breakdown: [
          { label: "TPS", rate: 5 },
          { label: "PST", rate: 7 },
        ],
      },
      {
        id: "SK", label: "Saskatchewan (TPS+PST)",
        rate: 11, fixed_label: "TPS+PST (SK)",
        breakdown: [
          { label: "TPS", rate: 5 },
          { label: "PST", rate: 6 },
        ],
      },
      {
        id: "MB", label: "Manitoba (TPS+RST)",
        rate: 12, fixed_label: "TPS+RST (MB)",
        breakdown: [
          { label: "TPS", rate: 5 },
          { label: "RST", rate: 7 },
        ],
      },
    ],
  },
  {
    code: "FR",
    label: "France",
    currency: "EUR",
    subregimes: [
      { id: "TVA_20",  label: "TVA 20% (taux normal)", rate: 20,  fixed_label: "TVA 20% (FR)" },
      { id: "TVA_10",  label: "TVA 10% (restauration, travaux)", rate: 10, fixed_label: "TVA 10% (FR)" },
      { id: "TVA_5_5", label: "TVA 5,5% (alimentation, livres)", rate: 5.5, fixed_label: "TVA 5,5% (FR)" },
      { id: "TVA_2_1", label: "TVA 2,1% (médicaments remboursables)", rate: 2.1, fixed_label: "TVA 2,1% (FR)" },
    ],
  },
  {
    code: "MA",
    label: "Maroc",
    currency: "MAD",
    subregimes: [
      { id: "TVA_20", label: "TVA 20% (taux normal)", rate: 20, fixed_label: "TVA 20% (MA)" },
      { id: "TVA_14", label: "TVA 14% (transport, banque)", rate: 14, fixed_label: "TVA 14% (MA)" },
      { id: "TVA_10", label: "TVA 10% (restauration, hôtellerie)", rate: 10, fixed_label: "TVA 10% (MA)" },
      { id: "TVA_7",  label: "TVA 7% (eau, électricité)", rate: 7,  fixed_label: "TVA 7% (MA)" },
    ],
  },
  {
    code: "US",
    label: "États-Unis",
    currency: "USD",
    exempt_by_default: true,
    subregimes: [],
  },
  {
    code: "OTHER",
    label: "Autre",
    currency: null, // user-defined
    custom: true,
    subregimes: [],
  },
];

export function findCountry(code) {
  return TAX_COUNTRIES.find((c) => c.code === code) || null;
}

export function findSubregime(countryCode, subregimeId) {
  const country = findCountry(countryCode);
  if (!country) return null;
  return country.subregimes.find((s) => s.id === subregimeId) || null;
}
